<template>
  <div class="card">
    <PageHeader
        :showBtn="false"
        title="Installment Due Report"
    >
      <template #extra-btn>
        <div class="d-flex align-items-center gap-1">
          <button class="btn btn-sm btn-outline-primary" @click="goToPrint">
            <i class="fas fa-print"></i> Print
          </button>
          <button class="btn btn-sm btn-outline-success" @click="exportReport">
            <i class="fas fa-file-excel"></i> Export
          </button>
        </div>
      </template>
    </PageHeader>

    <div class="px-2 mt-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-auto d-flex align-items-end mt-2">
          <FilterPopover
              ref="filterPopoverRef"
              title="Filters"
              :active-count="activeFilterCount"
              @open="onOpenFilter"
              @apply="onApplyFilters"
              @clear="onClearFilters"
          >
            <div class="col-12">
              <label class="filter-label">Marketing Team</label>
              <AsyncSelect
                  placeholder="Select Marketing Team"
                  v-model="draftFilters.employee"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ roles: 'employee' }"
              />
            </div>
            <div class="col-12">
              <label class="filter-label">Shareholder</label>
              <AsyncSelect
                  placeholder="Select Shareholder"
                  v-model="draftFilters.shareholder"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ roles: 'shareholder' }"
              />
            </div>
          </FilterPopover>
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-2 mt-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover">
          <thead>
          <tr class="text-center">
            <th>ID</th>
            <th>Shareholder Name</th>
            <th>Mobile No</th>
            <th>Marketing Team (Ref Name &amp; ID)</th>
            <th>Total Payable</th>
            <th>Booking Amount / Down Payment</th>
            <th>Remaining</th>
            <th>Number Of Installments</th>
            <th>Monthly Installment</th>
            <th v-for="head in monthHeads" :key="head.key">{{ head.label }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in tableItems" :key="item.shareholder_id">
            <td>{{ cleanText(item.serial_no) }}</td>
            <td>{{ cleanText(item.shareholder_name) }}</td>
            <td>{{ cleanText(item.mobile_no) }}</td>
            <td>{{ getEmployeeLabel(item) }}</td>
            <td class="text-end">{{ commaFormat(item.total_payable) }}</td>
            <td class="text-end">{{ commaFormat(item.booking_amount) }}</td>
            <td class="text-end">{{ commaFormat(item.remaining) }}</td>
            <td class="text-end">{{ item.number_of_installment }}</td>
            <td class="text-end">{{ commaFormat(item.monthly_installment) }}</td>
            <td class="text-end" v-for="head in monthHeads" :key="head.key">
              {{ monthCellText(item, head.key) }}
            </td>
          </tr>
          </tbody>
          <tfoot v-if="hasItems">
          <tr class="fw-bold">
            <td colspan="4" class="text-end">Total</td>
            <td class="text-end">{{ commaFormat(totals.total_payable) }}</td>
            <td class="text-end">{{ commaFormat(totals.booking_amount) }}</td>
            <td class="text-end">{{ commaFormat(totals.remaining) }}</td>
            <td class="text-end">{{ totals.number_of_installment }}</td>
            <td class="text-end">{{ commaFormat(totals.monthly_installment) }}</td>
            <td class="text-end" v-for="head in monthHeads" :key="head.key">
              {{ totalCellText(head.key) }}
            </td>
          </tr>
          </tfoot>
        </table>
        <p v-if="!hasItems" class="text-center mt-5">No data found</p>
      </div>
    </div>

    <div class="px-2 mb-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import PageHeader from '@/components/atom/PageHeader.vue'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import Pagination from '@/components/atom/Pagination.vue'
import Loader from '@/components/atom/LoaderComponent.vue'
import handleSdm from '@/services/modules/sdm/sdm'
import handleContact from '@/services/modules/contact'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import figureFormatter from '@/services/utils/figureFormatter'
import installmentDueReportPdf from '@/services/utils/pdf/installmentDueReportPdf'
import { resetPaginationPage } from '@/services/utils/global.js'

const $route = useRoute()
const $router = useRouter()
const $store = useStore()

const showError = inject('showError')

const { fetchInstallmentDueReport } = handleSdm()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany()
const { fetchBarcode } = handleBarcode()
const { commaFormat } = figureFormatter()
const { exportToPDF } = installmentDueReportPdf()

const loading = ref(false)
const offset = ref(20)
const tableItems = ref([])
const monthHeads = ref([])
const totals = ref(getEmptyTotals())
const companyInfo = ref({})
const filterPopoverRef = ref(null)

function getEmptyTotals() {
  return {
    no_of_shareholders: 0,
    total_payable: 0,
    booking_amount: 0,
    remaining: 0,
    number_of_installment: 0,
    monthly_installment: 0,
    months: {}
  }
}

const getEmptyFilters = () => {
  return {
    employee: '',
    shareholder: ''
  }
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const hasItems = computed(() => tableItems.value.length > 0)

const activeFilterCount = computed(() => {
  return Object.values(appliedFilters.value).filter((value) => !! value).length
})

const cleanText = (value) => {
  if (! value) return ''
  return value
}

const getEmployeeLabel = (item) => {
  if (! item.employee_name) return ''
  return `${item.employee_name} (${item.employee_id})`
}

const getMonthCell = (item, key) => {
  if (! item.months) return 0
  if (! item.months[key]) return 0
  return item.months[key]
}

const getTotalCell = (key) => {
  if (! totals.value.months) return 0
  if (! totals.value.months[key]) return 0
  return totals.value.months[key]
}

const monthCellText = (item, key) => {
  const value = getMonthCell(item, key)
  if (! value) return '-'
  return commaFormat(value)
}

const totalCellText = (key) => {
  const value = getTotalCell(key)
  if (! value) return '-'
  return commaFormat(value)
}

const buildFilterText = () => {
  const filters = appliedFilters.value
  const parts = []
  if (filters.employee) parts.push('Marketing Team: ' + filters.employee.name)
  if (filters.shareholder) parts.push('Shareholder: ' + filters.shareholder.name)
  if (! parts.length) return 'Filters: All'
  return 'Filters: ' + parts.join(' | ')
}

const buildQuery = (isAll) => {
  const filters = appliedFilters.value
  let query = '?company_id=' + $route.params.companyId
  if ($route.query.start) query += '&start_date=' + $route.query.start
  if ($route.query.end) query += '&end_date=' + $route.query.end
  if (filters.employee) query += '&employee_id=' + filters.employee.id
  if (filters.shareholder) query += '&shareholder_id=' + filters.shareholder.id
  if (isAll) return query + '&is_all=1'
  query += '&offset=' + offset.value
  if ($route.query.page) query += '&page=' + $route.query.page
  return query
}

const resetTable = () => {
  tableItems.value = []
  monthHeads.value = []
  totals.value = getEmptyTotals()
  $store.commit('resetPagination')
}

const loadData = async () => {
  loading.value = true
  try {
    const res = await fetchInstallmentDueReport(buildQuery(false))
    if (! res.status) {
      resetTable()
      return
    }
    tableItems.value = res.data.rows
    monthHeads.value = res.data.month_heads
    totals.value = res.data.totals
    $store.commit('setPaginationData', {
      records: res.data.total,
      perPage: res.data.per_page,
      currentPage: res.data.current_page
    })
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const onClickGo = async () => {
  await resetPage()
  await loadData()
}

const resetPage = async () => {
  resetPaginationPage($store, $route, $router)
  await $router.isReady()
}

const onOpenFilter = () => {
  draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = async () => {
  appliedFilters.value = Object.assign({}, draftFilters.value)
  await resetPage()
  await loadData()
}

const onClearFilters = async () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  if (filterPopoverRef.value) filterPopoverRef.value.close()
  await resetPage()
  await loadData()
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  await $router.push({ path: $route.path, query: routeQuery })
  await loadData()
}

const goToPrint = async () => {
  loading.value = true
  try {
    const res = await fetchInstallmentDueReport(buildQuery(true))
    if (! res.status) {
      showError('No data found')
      return
    }
    const qrcodeData = JSON.stringify({
      created_by: $store.state.user.user.name,
      created_date_time: new Date().toISOString()
    })
    const barcodeRes = await fetchBarcode(`?code=installment-due-report&qrcode=${qrcodeData}`)
    exportToPDF(companyInfo.value, res.data, $route.query.start, $route.query.end, barcodeRes.qrcode, buildFilterText())
  } catch (err) {
    showError('Failed to generate PDF')
  } finally {
    loading.value = false
  }
}

const exportReport = () => {
  const token = localStorage.getItem('token')
  const query = buildQuery(true) + '&is_export=1&_token=' + token
  const url = `${process.env.VUE_APP_BASE_URL}/export/sdm/installment-due-report${query}`
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.click()
}

const getCompanyInfo = async () => {
  const res = await fetchCompanyInfo($route.params.companyId)
  if (! res.status) return
  companyInfo.value = res.data
}

onMounted(() => {
  getCompanyInfo()
  loadData()
})
</script>
